import { FLUTTERWAVE_CONFIG } from '../config/flutterwave';
import { PaymentItem } from '../types';

// Get supported payment methods
export const getSupportedPaymentMethods = () => {
  return [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      type: 'card' as const,
      description: 'Pay with Visa, Mastercard, or any international card',
      icon: '💳',
      processingTime: 'Instant',
      fees: 0,
      isActive: true,
    },
    {
      id: 'mobile_money',
      name: 'Mobile Money',
      type: 'mobile_money' as const,
      description: 'Pay with MTN Mobile Money, Vodafone Cash, or Airtel Money',
      icon: '📱',
      processingTime: 'Instant',
      fees: 0,
      isActive: true,
    },
    {
      id: 'bank_transfer',
      name: 'Bank Transfer',
      type: 'bank_transfer' as const,
      description: 'Direct bank transfer to our account',
      icon: '🏦',
      processingTime: '1-3 business days',
      fees: 0,
      isActive: true,
    },
    {
      id: 'offline',
      name: 'Offline Payment',
      type: 'offline' as const,
      description: 'Pay directly to our accounts and notify us',
      icon: '💸',
      processingTime: '1-24 hours (after verification)',
      fees: 0,
      isActive: true,
    },
  ];
};

// Generate transaction reference (alias for generateTxRef for backward compatibility)
export const generateTransactionReference = (prefix: string = 'PS') => {
  return generateTxRef(prefix);
};

// Format amount for Flutterwave (amount should be in Ghana Cedis)
export const formatAmountForFlutterwave = (amount: number): number => {
  // Flutterwave expects amount in the main currency unit (Ghana Cedis)
  // No conversion needed - send the amount as is
  return Math.round(amount);
};

// Flutterwave payment initialization
export const initializeFlutterwavePayment = (
  txRef: string,
  amount: number,
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  },
  items: PaymentItem[],
  userId: string,
  referredBy?: string
) => {
  // Validate inputs
  if (!txRef || !amount || !customerInfo || !items || !userId) {
    throw new Error('Missing required parameters for Flutterwave payment');
  }

  // Prepare customer data
  const customer = {
    email: customerInfo.email,
    phone_number: customerInfo.phone,
    name: `${customerInfo.firstName} ${customerInfo.lastName}`,
    address: customerInfo.address,
    city: customerInfo.city,
    state: customerInfo.state,
    postal_code: customerInfo.postalCode,
    country: customerInfo.country
  };

  // Prepare meta data for IPN
  const meta = {
    userId: userId,
    referredBy: referredBy,
    customerInfo: customerInfo,
    items: items,
    address: customerInfo.address,
    city: customerInfo.city,
    state: customerInfo.state,
    postalCode: customerInfo.postalCode,
    country: customerInfo.country,
    paymentSource: 'web',
    timestamp: new Date().toISOString()
  };

  // Flutterwave payment configuration
  const paymentConfig = {
    tx_ref: txRef,
    amount: amount,
    currency: FLUTTERWAVE_CONFIG.defaultCurrency,
    redirect_url: FLUTTERWAVE_CONFIG.redirectUrl,
    ipn_url: FLUTTERWAVE_CONFIG.ipnUrl,
    customer: customer,
    meta: meta,
    customizations: {
      title: FLUTTERWAVE_CONFIG.appName,
      description: `Payment for ${items.length} item(s)`,
      logo: FLUTTERWAVE_CONFIG.logoUrl
    },
    payment_options: FLUTTERWAVE_CONFIG.supportedPaymentMethods.join(','),
    country: FLUTTERWAVE_CONFIG.defaultCountry,
    email: customerInfo.email,
    phone_number: customerInfo.phone,
    name: customer.name
  };

  return paymentConfig;
};

// Alternative initialization function that accepts a single payment data object
export const initializeFlutterwavePaymentFromData = (paymentData: any) => {
  // This function accepts the payment data object format used in Checkout.tsx
  // and converts it to the format expected by the main function
  
  const {
    tx_ref,
    amount,
    currency,
    redirect_url,
    customer,
    customizations,
    meta
  } = paymentData;

  // Extract customer info from the customer object
  const customerInfo = {
    firstName: customer.name.split(' ')[0] || '',
    lastName: customer.name.split(' ').slice(1).join(' ') || '',
    email: customer.email || '',
    phone: customer.phone_number || '',
    address: meta?.address || '',
    city: meta?.city || '',
    state: meta?.state || '',
    postalCode: meta?.postalCode || '',
    country: meta?.country || 'GH'
  };

  // Extract items from meta
  const items = meta?.orderItems ? [{
    type: 'course', // Default type, adjust as needed
    id: meta?.itemIds?.[0] || 'unknown',
    name: customizations?.description || 'Course purchase',
    amount: amount / 100 // Convert from pesewas back to cedis
  }] : [];

  const userId = meta?.userId || '';
  
  // Debug logging to ensure userId is properly extracted
  console.log('Flutterwave utils - Extracted userId:', userId);
  console.log('Flutterwave utils - Full meta data:', meta);
  
  // Validate that userId is present
  if (!userId) {
    console.error('Flutterwave utils - Missing userId in meta data');
    throw new Error('userId is required in meta data for payment processing');
  }

  return {
    public_key: FLUTTERWAVE_CONFIG.publicKey,
    tx_ref,
    amount,
    currency,
    redirect_url,
    ipn_url: FLUTTERWAVE_CONFIG.ipnUrl,
    customer,
    meta: {
      ...meta,
      userId: userId, // Ensure userId is explicitly included
      source: 'web',
      timestamp: new Date().toISOString()
    },
    customizations,
    payment_options: FLUTTERWAVE_CONFIG.supportedPaymentMethods.join(','),
    country: FLUTTERWAVE_CONFIG.defaultCountry,
    email: customer.email,
    phone_number: customer.phone_number,
    name: customer.name
  };
};

// Generate unique transaction reference
export const generateTxRef = (prefix: string = 'PS') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}_${timestamp}_${random}`.toUpperCase();
};

// Validate Flutterwave response
export const validateFlutterwaveResponse = (response: any) => {
  if (!response) {
    throw new Error('No response received from Flutterwave');
  }

  if (response.status !== 'success') {
    throw new Error(`Payment failed: ${response.message || 'Unknown error'}`);
  }

  if (!response.data || !response.data.tx_ref) {
    throw new Error('Invalid response format from Flutterwave');
  }

  return response.data;
};

// Handle successful payment
export const handleSuccessfulPayment = (paymentData: any) => {
  // This function will be called when payment is successful
  // The actual processing is done by the IPN endpoint
  console.log('Payment successful:', paymentData);
  
  // You can add any client-side logic here
  // For example, showing success message, redirecting, etc.
  
  return {
    success: true,
    txRef: paymentData.tx_ref,
    transactionId: paymentData.id,
    amount: paymentData.amount,
    currency: paymentData.currency
  };
};

// Handle payment error
export const handlePaymentError = (error: any) => {
  console.error('Payment error:', error);
  
  return {
    success: false,
    error: error.message || 'Payment failed',
    code: error.code || 'UNKNOWN_ERROR'
  };
};

// Get payment status
export const getPaymentStatus = async (txRef: string) => {
  try {
    // You can implement a function to check payment status
    // This would typically call your backend or Flutterwave API
    console.log('Checking payment status for:', txRef);
    
    // For now, return a mock response
    return {
      status: 'pending',
      message: 'Payment status check not implemented yet'
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

// Client-side payment validation functions
export const verifyPaymentClientSide = async (txRef: string): Promise<{
  success: boolean;
  data?: any;
  error?: string;
  status: 'successful' | 'pending' | 'failed' | 'cancelled';
}> => {
  try {
    // Use Flutterwave's verification API directly from client
    const response = await fetch(`${FLUTTERWAVE_CONFIG.baseUrl}/transactions/verify_by_reference?tx_ref=${txRef}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${FLUTTERWAVE_CONFIG.secretKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.status === 'success' && result.data) {
      const transaction = result.data;
      
      // Validate transaction details
      const isValid = validateTransactionData(transaction);
      
      if (!isValid) {
        return {
          success: false,
          error: 'Transaction data validation failed',
          status: 'failed'
        };
      }

      return {
        success: true,
        data: transaction,
        status: transaction.status
      };
    } else {
      return {
        success: false,
        error: result.message || 'Payment verification failed',
        status: 'failed'
      };
    }
  } catch (error) {
    console.error('Payment verification error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 'failed'
    };
  }
};

// Validate transaction data integrity
const validateTransactionData = (transaction: any): boolean => {
  try {
    // Check required fields
    if (!transaction.tx_ref || !transaction.id || !transaction.amount || !transaction.currency) {
      console.error('Missing required transaction fields');
      return false;
    }

    // Validate currency (should be GHS)
    if (transaction.currency !== 'GHS') {
      console.error('Invalid currency:', transaction.currency);
      return false;
    }

    // Validate amount format (should be in pesewas)
    if (typeof transaction.amount !== 'number' || transaction.amount <= 0) {
      console.error('Invalid amount:', transaction.amount);
      return false;
    }

    // Validate status
    const validStatuses = ['successful', 'pending', 'failed', 'cancelled'];
    if (!validStatuses.includes(transaction.status)) {
      console.error('Invalid transaction status:', transaction.status);
      return false;
    }

    // Validate customer information
    if (!transaction.customer || !transaction.customer.email) {
      console.error('Missing customer information');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Transaction validation error:', error);
    return false;
  }
};

// Enhanced payment status checking with retry logic
export const checkPaymentStatusWithRetry = async (
  txRef: string, 
  maxRetries: number = 3, 
  delayMs: number = 2000
): Promise<{
  success: boolean;
  data?: any;
  error?: string;
  status: 'successful' | 'pending' | 'failed' | 'cancelled';
}> => {
  let lastError: string | undefined;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Payment status check attempt ${attempt}/${maxRetries} for txRef: ${txRef}`);
      
      const result = await verifyPaymentClientSide(txRef);
      
      if (result.success) {
        console.log(`Payment verification successful on attempt ${attempt}`);
        return result;
      }
      
      // If payment is still pending, wait before retrying
      if (result.status === 'pending' && attempt < maxRetries) {
        console.log(`Payment still pending, waiting ${delayMs}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        continue;
      }
      
      // If payment failed or was cancelled, no need to retry
      if (result.status === 'failed' || result.status === 'cancelled') {
        return result;
      }
      
      lastError = result.error;
      
    } catch (error) {
      lastError = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error(`Payment verification attempt ${attempt} failed:`, lastError);
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }
  
  return {
    success: false,
    error: `Payment verification failed after ${maxRetries} attempts. Last error: ${lastError}`,
    status: 'failed'
  };
};

// Process successful payment on client side
export const processSuccessfulPayment = async (
  transactionData: any,
  orderData: any
): Promise<{
  success: boolean;
  orderId?: string;
  error?: string;
}> => {
  try {
    // Validate transaction data
    if (!transactionData || transactionData.status !== 'successful') {
      throw new Error('Invalid transaction data or status');
    }

    // Create order in Firestore
    const orderId = await createOrderInFirestore(transactionData, orderData);
    
    // Clear cart and pending order
    localStorage.removeItem('pendingOrder');
    
    // Update user's enrolled courses/books
    await updateUserEnrollments(orderData.items, orderData.userId);
    
    return {
      success: true,
      orderId
    };
    
  } catch (error) {
    console.error('Error processing successful payment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to process payment'
    };
  }
};

// Create order in Firestore
const createOrderInFirestore = async (transactionData: any, orderData: any): Promise<string> => {
  try {
    const { db } = await import('../firebase/config');
    const { doc, setDoc } = await import('firebase/firestore');
    
    const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const order = {
      id: orderId,
      userId: orderData.userId,
      items: orderData.items,
      total: orderData.total,
      currency: 'GHS',
      status: 'completed',
      paymentMethod: orderData.paymentMethod,
      paymentReference: transactionData.tx_ref,
      transactionId: transactionData.id,
      flutterwaveTransactionId: transactionData.id,
      shippingAddress: orderData.customerInfo,
      billingAddress: orderData.customerInfo,
      createdAt: new Date(),
      updatedAt: new Date(),
      completedAt: new Date(),
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      notes: `Payment verified via Flutterwave. Transaction ID: ${transactionData.id}`,
      referredBy: orderData.referredBy,
    };

    // Save to user's orders subcollection
    const userOrderRef = doc(db, 'users', orderData.userId, 'orders', orderId);
    await setDoc(userOrderRef, order);
    
    // Also save to main orders collection for admin access
    const mainOrderRef = doc(db, 'orders', orderId);
    await setDoc(mainOrderRef, order);
    
    // Process referral commission if applicable - only after order is successfully saved
    if (orderData.referredBy) {
      try {
        console.log('Flutterwave - Processing referral commission for referrer:', orderData.referredBy);
        
        // Import referral commission functions
        const { processReferralCommission } = await import('./referralCommission');
        
        // Process the referral commission
        const commissionId = await processReferralCommission(
          orderData.referredBy,
          orderId,
          transactionData.tx_ref,
          orderData.items,
          orderData.total
        );
        
        console.log('Flutterwave - Referral commission processed successfully. Commission ID:', commissionId);
      } catch (error) {
        console.error('Flutterwave - Error processing referral commission:', error);
        // Don't fail the order creation if commission processing fails
        // The order is still valid even if commission processing fails
      }
    } else {
      console.log('Flutterwave - No referral commission to process (user was not referred)');
    }
    
    return orderId;
  } catch (error) {
    console.error('Error creating order in Firestore:', error);
    throw error;
  }
};

// Update user enrollments
const updateUserEnrollments = async (items: any[], userId: string): Promise<void> => {
  try {
    const { db } = await import('../firebase/config');
    const { doc, updateDoc, arrayUnion } = await import('firebase/firestore');
    
    const userRef = doc(db, 'users', userId);
    
    // Update enrolled courses
    const courseIds = items.filter(item => item.type === 'course').map(item => item.itemId);
    if (courseIds.length > 0) {
      await updateDoc(userRef, {
        enrolledCourses: arrayUnion(...courseIds),
        updatedAt: new Date()
      });
    }
    
    // Update purchased books
    const bookIds = items.filter(item => item.type === 'book').map(item => item.itemId);
    if (bookIds.length > 0) {
      await updateDoc(userRef, {
        purchasedBooks: arrayUnion(...bookIds),
        updatedAt: new Date()
      });
    }
    
  } catch (error) {
    console.error('Error updating user enrollments:', error);
    // Don't throw error as this is not critical for payment success
  }
};

// Enhanced payment initialization with client-side validation
export const initializeFlutterwavePaymentWithValidation = (
  txRef: string,
  amount: number,
  customerInfo: any,
  items: PaymentItem[],
  userId: string,
  referredBy?: string
) => {
  // Validate inputs
  if (!txRef || !amount || !customerInfo || !items || !userId) {
    throw new Error('Missing required parameters for Flutterwave payment');
  }

  // Prepare customer data
  const customer = {
    email: customerInfo.email,
    phone_number: customerInfo.phone,
    name: `${customerInfo.firstName} ${customerInfo.lastName}`,
    address: customerInfo.address || '',
    city: customerInfo.city || '',
    state: customerInfo.state || '',
    postal_code: customerInfo.postalCode || '',
    country: customerInfo.country || 'GH'
  };

  // Prepare meta data for client-side validation
  const meta = {
    userId: userId,
    referredBy: referredBy,
    customerInfo: customerInfo,
    items: items,
    address: customerInfo.address || '',
    city: customerInfo.city || '',
    state: customerInfo.state || '',
    postalCode: customerInfo.postalCode || '',
    country: customerInfo.country || 'GH',
    paymentSource: 'web',
    timestamp: new Date().toISOString(),
    validationRequired: true // Flag for client-side validation
  };

  // Flutterwave payment configuration
  const paymentConfig = {
    public_key: FLUTTERWAVE_CONFIG.publicKey,
    tx_ref: txRef,
    amount: formatAmountForFlutterwave(amount),
    currency: FLUTTERWAVE_CONFIG.defaultCurrency,
    redirect_url: FLUTTERWAVE_CONFIG.redirectUrl,
    customer: customer,
    meta: meta,
    customizations: {
      title: FLUTTERWAVE_CONFIG.appName,
      description: `Payment for ${items.length} item(s)`,
      logo: FLUTTERWAVE_CONFIG.logoUrl
    },
    payment_options: FLUTTERWAVE_CONFIG.supportedPaymentMethods.join(','),
    country: FLUTTERWAVE_CONFIG.defaultCountry,
    email: customerInfo.email,
    phone_number: customerInfo.phone,
    name: customer.name
  };

  return paymentConfig;
};
